/** The strip above the chain: where the index was, and what the chain
 * implies about it, at the minute being replayed.
 *
 * An INLINE strip, not a row of cards. Six stat cards cost ~110px of
 * height to carry six numbers, and this page has spent the whole of its
 * design fighting for vertical room for the chain and the basket. The
 * numbers are short and the labels are short; on one line they read just
 * as well and cost a fifth of the space.
 *
 * `Synth Fut` is put-call parity at the ATM rather than a quoted future.
 * It is labelled that way on purpose — real futures are archived only from
 * 2026-08-31, so for every earlier session this is the only future there
 * is, and calling it "Fut" would imply a price nobody recorded.
 */

import type { ReactElement } from "react";
import type { SimChain } from "@/lib/simulatorApi";

function num(value: number | null, digits = 2): string {
  return value === null ? "—" : value.toFixed(digits);
}

interface StatProps {
  label: string;
  value: string;
  hint?: string;
}

function Stat({ label, value, hint }: Readonly<StatProps>): ReactElement {
  // ⭐ The hint stays VISIBLE when the value is absent, and becomes a
  // tooltip when it is present. A dash with no explanation is the failure
  // this page has guarded against everywhere else — "PCR —" reads as a bug
  // until it says the session predates OI capture. When there IS a number,
  // the caveat is a qualifier ("put-call parity", "nearest listed strike")
  // that matters on inspection, not at a glance, so it costs no width.
  const missing = value === "—";
  return (
    <span className="flex items-baseline gap-1.5" title={hint}>
      <span className="text-[11px] text-ink-secondary">{label}</span>
      <span className="font-mono text-sm tabular-nums text-ink">{value}</span>
      {missing && hint !== undefined && <span className="text-[11px] text-ink-muted">{hint}</span>}
    </span>
  );
}

/** ⭐ The chain on screen is the PREVIOUS minute's until the next lands
 * (`keepPreviousData` in `useSimulatorState`), and a session Athena has not
 * cached takes 1-3 s to arrive — measured up to 17 s on a cold one. Without a
 * cue the numbers simply sit there and a step looks like it did nothing
 * (operator, 2026-09-23: *"I am not getting whether its changing or not"*).
 * So a fetch in flight says so here, and the stale figures are dimmed. */
function Loading(): ReactElement {
  return (
    <span className="pill bg-surface-2 text-ink-secondary" role="status">
      <i className="ti ti-loader-2 animate-spin text-sm" /> Loading
    </span>
  );
}

interface ChainHeaderProps {
  chain: SimChain;
  loading?: boolean;
}

export function ChainHeader({ chain, loading = false }: Readonly<ChainHeaderProps>): ReactElement {
  const atm = chain.atm_strike;
  return (
    <div
      className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-y border-line py-1.5"
      aria-busy={loading}
    >
      {loading && <Loading />}
      <Stat label="Spot" value={num(chain.spot)} />
      <Stat
        label="ATM strike"
        value={atm === null ? "—" : String(atm)}
        hint="nearest listed strike"
      />
      <Stat label="Synth Fut" value={num(chain.synthetic_future)} hint="put-call parity" />
      <Stat label="Straddle" value={num(chain.straddle_premium)} hint="ATM call + put" />
      <Stat
        label="ATM IV"
        value={chain.atm_iv === null ? "—" : `${(chain.atm_iv * 100).toFixed(1)}%`}
      />
      <Stat
        label="PCR"
        value={num(chain.pcr)}
        hint={chain.pcr === null ? "not captured that session" : "put / call OI"}
      />
    </div>
  );
}
